import type { UserGroupDataQualityIssueRecord, UserGroupDataQualityRecord } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { NoddiInfoBanner } from 'noddi-ui-web';
import type { ReactNode } from 'react';
import {
  defaultUserGroupDataQualityTexts,
  getUserGroupDataQualityIssueLabel,
  isSharedPhoneNumberIssue,
  type UserGroupDataQualityPromptTranslations
} from './userGroupDataQualityTexts';

type Props = {
  className?: string;
  /** Undefined while the report is loading or unavailable; the banner then renders nothing. */
  record: UserGroupDataQualityRecord | undefined;
  hasError?: boolean;
  /** Renders the per-member list. The backend only fills `members` for group admins. */
  showMembers?: boolean;
  /** Drops this user from the member list so their issues stay on the group banner. */
  excludeMemberUserId?: number;
  renderAction?: (userGroup: UserGroupDataQualityRecord) => ReactNode;
  translations?: UserGroupDataQualityPromptTranslations;
  /** Group-level issues a surface cannot act on can be hidden. Member rows are not filtered. */
  isVisibleIssue?: (issue: UserGroupDataQualityIssueRecord) => boolean;
  /** Member details belong to each member, so hosts can link to their self-service manager. */
  renderMemberAction?: (userGroup: UserGroupDataQualityRecord) => ReactNode;
};

/**
 * Renders a data-quality report. Owns no query, so a surface that already holds
 * the record (the customer checkout provider) shows it without fetching again.
 */
export const UserGroupDataQualityBanner = ({
  className,
  record,
  hasError,
  showMembers,
  excludeMemberUserId,
  renderAction,
  translations,
  isVisibleIssue,
  renderMemberAction
}: Props) => {
  const texts = translations ?? defaultUserGroupDataQualityTexts;

  if (hasError) {
    return (
      <NoddiInfoBanner
        className={className}
        variant='error'
        title={texts.errorTitle}
        texts={[{ value: texts.errorMessage }]}
      />
    );
  }

  if (!record) {
    return null;
  }

  const reportedIssues = (issues: readonly UserGroupDataQualityIssueRecord[]) => {
    const actionable = issues.filter((issue) => !isSharedPhoneNumberIssue(issue));
    return isVisibleIssue ? actionable.filter(isVisibleIssue) : actionable;
  };
  const groupIssues = record.isValid ? [] : reportedIssues(record.issues);
  const memberRows = showMembers
    ? (record.members ?? []).flatMap((member) => {
        if (member.userId === excludeMemberUserId) {
          return [];
        }
        const issues = reportedIssues(member.issues);
        return issues.length === 0 ? [] : [{ member, issues }];
      })
    : [];

  if (groupIssues.length === 0 && memberRows.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <p className='text-sm text-muted-foreground'>{texts.description}</p>
      {groupIssues.length > 0 && (
        <NoddiInfoBanner
          variant='warning'
          title={texts.title(record)}
          texts={groupIssues.map((issue) => ({ value: getUserGroupDataQualityIssueLabel(texts, issue) }))}
          actionNode={renderAction?.(record)}
        />
      )}
      {memberRows.length > 0 && (
        <NoddiInfoBanner
          variant='warning'
          title={texts.membersTitle}
          texts={memberRows.map(({ member, issues }) => ({
            value: `${member.firstName} ${member.lastName} — ${issues
              .map((issue) => getUserGroupDataQualityIssueLabel(texts, issue))
              .join(', ')}`
          }))}
          actionNode={renderMemberAction?.(record)}
        />
      )}
    </div>
  );
};
